import { sameEmail, VENDOR, type ConsentsDto, type Locale, type MeResponse, type MemoryDto, type PermissionRuleDto, type PreferencesDto, type UserType } from '@dbrain/shared';
import { Alert, Badge, Button, Dialog, EmptyState, Field, Icons, Select, Skeleton, Switch, TextArea, TextInput, useTheme, type ThemePreference } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../../experience.js';
import { Link, useNavigate } from 'react-router';
import { api } from '../../api/client.js';
import { keys, useMe, useMemories, useModels, usePreferences, useProviders, useRoutes, useSessions, useTenant } from '../../api/hooks.js';
import { setLocale } from '../../i18n/index.js';
import { ownDraft, ownDraftTest, providerEndpoints } from '../../pages/admin/ModelsAdminPage.js';
import { timeZones } from '../../time-zone.js';
import { AddProviderInline, modelName, modelUsage, ProviderCard, taskStates } from '../admin/providers.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { LinkedAccountsGroup } from '../microsoft365/LinkedAccounts.js';
import { SettingGroup, SettingRow } from './rows.js';
import { useMeta } from '../../experience.js';

/**
 * The sections of the settings dialog (docs/design/mockups/Ayarlar.dc.html):
 * Genel, Hesap, Bildirimler, Hafıza and Firma for everyone; the
 * organization's limits, policies, e-mail and audit log for its admins.
 */

function useSavePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<PreferencesDto>) => api<PreferencesDto>('PATCH', '/api/me/preferences', body),
    onSuccess: (data) => queryClient.setQueryData(keys.preferences, data),
  });
}

/** The user's own default model for one task (D-27, MM-009), within the organization's allowed models. */
function DefaultModelSelect({ task, id, describedBy }: { task: 'main' | 'code'; id: string; describedBy?: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const models = useModels();
  const routes = useRoutes();
  const own = (routes.data ?? []).find((r) => r.scope === 'user' && r.callType === task)?.modelRef ?? '';
  const set = useMutation({
    mutationFn: (modelRef: string) =>
      modelRef ? api('PUT', `/api/model-routes/${task}`, { scope: 'user', modelRef }) : api('DELETE', `/api/model-routes/${task}?scope=user`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.models });
      void queryClient.invalidateQueries({ queryKey: keys.routes });
    },
  });
  const options = (models.data?.models ?? []).filter((m) => m.kind === 'chat' && m.enabled && !m.missingSince && m.allowed !== false);
  const inherited = models.data?.routes[task];
  const inheritedModel = inherited ? options.find((m) => m.id === inherited.modelRef) : undefined;
  return (
    <>
      <Select id={id} aria-describedby={describedBy} value={own} disabled={!models.data || set.isPending} onChange={(e) => set.mutate(e.target.value)} data-testid={`default-model-${task}`}>
        <option value="">{inheritedModel && !own ? t('settingsDialog.general.orgModel', { model: modelName(inheritedModel) }) : t('settingsDialog.general.orgChoice')}</option>
        {options.map((m) => (
          <option key={m.id} value={m.id}>
            {modelName(m)}
          </option>
        ))}
      </Select>
      <ErrorAlert error={set.error ?? models.error} />
    </>
  );
}

/**
 * Verilerimi sil (D-38): a local account confirms with its password; a company-directory account has no password
 * here and types its e-mail address instead (D-187).
 */
function DeleteDataDialog({ me, onClose }: { me: MeResponse; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const directory = me.account?.kind === 'directory';
  const [secret, setSecret] = useState('');
  const [confirm, setConfirm] = useState(false);
  const ready = directory ? sameEmail(secret, me.user.email) : secret.length > 0;
  const remove = useMutation({
    mutationFn: () =>
      api<{ conversations: number; memories: number; documents: number }>('POST', '/api/me/delete-data', { ...(directory ? { email: secret } : { password: secret }), confirm: true }),
    onSuccess: () => {
      setSecret('');
      setConfirm(false);
      void queryClient.invalidateQueries();
    },
  });
  return (
    <Dialog title={t('settings.data.deleteTitle')} closeLabel={t('common.close')} onClose={onClose} data-testid="delete-data-dialog">
      <p className="muted">{t('settings.data.deleteHint')}</p>
      {remove.isSuccess ? (
        <Alert tone="success" title={t('settings.data.deleted', { conversations: remove.data.conversations, memories: remove.data.memories, documents: remove.data.documents ?? 0 })} />
      ) : null}
      <ErrorAlert error={remove.error} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (confirm && ready) remove.mutate();
        }}
      >
        {directory ? (
          <Field label={t('settings.data.email')} hint={t('settings.data.emailHint', { email: me.user.email })}>
            {({ id, describedBy }) => (
              <TextInput
                id={id}
                aria-describedby={describedBy}
                type="email"
                autoComplete="off"
                spellCheck={false}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                data-testid="delete-data-email"
              />
            )}
          </Field>
        ) : (
          <Field label={t('auth.password')}>
            {({ id }) => <TextInput id={id} type="password" autoComplete="current-password" value={secret} onChange={(e) => setSecret(e.target.value)} data-testid="delete-data-password" />}
          </Field>
        )}
        <label className="ui-check">
          <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="delete-data-confirm" />
          <span>{t('settings.data.confirm')}</span>
        </label>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={onClose}>
            {remove.isSuccess ? t('common.close') : t('common.cancel')}
          </Button>
          <Button type="submit" variant="danger" busy={remove.isPending} disabled={!confirm || !ready || remove.isSuccess} data-testid="delete-data">
            {t('settings.data.delete')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export function GeneralSection({ me }: { me: MeResponse }) {
  const { t, i18n } = useTranslation();
  const capabilities = useCapabilities();
  const queryClient = useQueryClient();
  const [theme, setTheme] = useTheme();
  const preferences = usePreferences();
  const save = useSavePreferences();
  const [instructions, setInstructions] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const meta = useMeta();
  const saveLocale = useMutation({
    mutationFn: (locale: Locale) => api<MeResponse>('PATCH', '/api/me', { locale }),
    onSuccess: async (data) => {
      queryClient.setQueryData(keys.me, data);
      await setLocale(data.user.locale);
    },
  });
  const data = preferences.data;
  const zones = timeZones();
  return (
    <div className="settings-rows" data-testid="settings-general">
      <ErrorAlert error={preferences.error ?? save.error ?? saveLocale.error} />
      <SettingRow label={t('settingsDialog.general.theme')}>
        {({ id }) => (
          <Select id={id} value={theme} onChange={(e) => setTheme(e.target.value as ThemePreference)} data-testid="settings-theme">
            <option value="light">{t('settingsDialog.general.themes.light')}</option>
            <option value="dark">{t('settingsDialog.general.themes.dark')}</option>
            <option value="system">{t('settingsDialog.general.themes.system')}</option>
          </Select>
        )}
      </SettingRow>
      <SettingRow label={t('settingsDialog.general.language')}>
        {({ id }) => (
          <Select id={id} value={i18n.language === 'en' ? 'en' : me.user.locale} disabled={saveLocale.isPending} onChange={(e) => saveLocale.mutate(e.target.value as Locale)} data-testid="settings-language">
            <option value="tr">{t('lang.tr')}</option>
            <option value="en">{t('lang.en')}</option>
          </Select>
        )}
      </SettingRow>
      <SettingRow label={t('settings.answers.timeZone')} hint={data?.timeZone ? t('settingsDialog.general.timeZoneChosen') : t('settingsDialog.general.timeZoneBrowser')}>
        {({ id, hintId }) => (
          <Select id={id} aria-describedby={hintId} value={data?.timeZone ?? ''} disabled={!data} onChange={(e) => save.mutate({ timeZone: e.target.value || null })} data-testid="time-zone">
            <option value="">{t('settings.answers.timeZoneAuto', { zone: data?.detectedTimeZone ?? data?.defaultTimeZone ?? '' })}</option>
            {(data?.timeZone && !zones.includes(data.timeZone) ? [data.timeZone, ...zones] : zones).map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </Select>
        )}
      </SettingRow>
      <SettingRow label={t('settingsDialog.general.defaultModel')} hint={t('settings.defaultModel.hint')}>
        {({ id, hintId }) => <DefaultModelSelect task="main" id={id} describedBy={hintId} />}
      </SettingRow>
      {capabilities.codeExperience ? <SettingRow label={t('settingsDialog.general.defaultCodeModel')}>{({ id }) => <DefaultModelSelect task="code" id={id} />}</SettingRow> : null}
      <SettingRow label={t('settings.answers.language')} hint={t('settings.answers.languageHint')}>
        {({ id, hintId }) => (
          <Select
            id={id}
            aria-describedby={hintId}
            value={data?.responseLanguage ?? 'auto'}
            disabled={!data}
            onChange={(e) => save.mutate({ responseLanguage: e.target.value as PreferencesDto['responseLanguage'] })}
            data-testid="response-language"
          >
            <option value="auto">{t('settings.answers.auto')}</option>
            <option value="tr">{t('lang.tr')}</option>
            <option value="en">{t('lang.en')}</option>
          </Select>
        )}
      </SettingRow>
      <SettingRow label={t('settings.answers.instructions')} hint={t('settings.answers.instructionsHint')} stacked>
        {({ id, hintId }) => (
          <form
            className="setting-row__form"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({ customInstructions: (instructions ?? '').trim() || null }, { onSuccess: () => setInstructions(null) });
            }}
          >
            <TextArea
              id={id}
              aria-describedby={hintId}
              maxLength={4000}
              value={instructions ?? data?.customInstructions ?? ''}
              onChange={(e) => setInstructions(e.target.value)}
              data-testid="custom-instructions"
            />
            <Button type="submit" size="sm" busy={save.isPending} disabled={instructions === null}>
              {t('common.save')}
            </Button>
          </form>
        )}
      </SettingRow>
      <SettingRow label={t('memory.title')} hint={data && !data.memoryAllowed ? t('memory.disabledByAdmin') : t('settingsDialog.general.memoryHint')}>
        {({ id, hintId }) => (
          <Switch
            id={id}
            aria-describedby={hintId}
            checked={Boolean(data?.memoryEnabled && data.memoryAllowed)}
            disabled={!data || !data.memoryAllowed || save.isPending}
            onChange={(e) => save.mutate({ memoryEnabled: e.target.checked })}
            data-testid="memory-enabled"
          />
        )}
      </SettingRow>
      <SettingRow label={t('settingsDialog.general.data')} hint={t('settingsDialog.general.dataHint')}>
        {() => (
          <div className="row">
            <a className="ui-button ui-button--secondary" href="/api/me/export" download="oppy-data.jsonl" data-testid="data-export">
              <Icons.download size={16} />
              {t('settingsDialog.general.download')}
            </a>
            <Button variant="danger" onClick={() => setDeleting(true)} data-testid="data-delete-open">
              {t('common.delete')}
            </Button>
          </div>
        )}
      </SettingRow>
      {meta.data?.release ? (
        // D-188: the server's release, as the phone's "Oppy adresi ve uygulama" says it
        <SettingRow label={t('settingsDialog.general.version')} hint={t('settingsDialog.general.versionHint', { vendor: VENDOR.name })} data-testid="settings-version">
          {() => <span className="settings-version">{meta.data?.release}</span>}
        </SettingRow>
      ) : null}
      {deleting ? <DeleteDataDialog me={me} onClose={() => setDeleting(false)} /> : null}
    </div>
  );
}

function ProfileGroup({ me }: { me: MeResponse }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(me.user.displayName);
  const [userType, setUserType] = useState<UserType>(me.user.userType ?? 'business');
  const save = useMutation({
    mutationFn: () => api<MeResponse>('PATCH', '/api/me', { displayName, userType }),
    onSuccess: (data) => queryClient.setQueryData(keys.me, data),
  });
  return (
    <SettingGroup title={t('settings.profile.title')}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <ErrorAlert error={save.error} />
        {save.isSuccess ? <Alert tone="success" title={t('common.saved')} /> : null}
        <div className="form-grid">
          <Field label={t('settings.profile.name')}>{({ id }) => <TextInput id={id} value={displayName} onChange={(e) => setDisplayName(e.target.value)} data-testid="profile-name" />}</Field>
          <Field label={t('settings.profile.userType')}>
            {({ id }) => (
              <Select id={id} value={userType} onChange={(e) => setUserType(e.target.value as UserType)}>
                <option value="business">{t('settings.profile.business')}</option>
                <option value="developer">{t('settings.profile.developer')}</option>
              </Select>
            )}
          </Field>
        </div>
        <p className="setting-inline">
          <span className="muted">{t('settings.email.title')}:</span> {me.user.email}{' '}
          <Badge tone={me.user.emailVerified ? 'success' : 'warning'}>{me.user.emailVerified ? t('settings.email.verified') : t('settings.email.unverified')}</Badge>
        </p>
        <Button type="submit" busy={save.isPending}>
          {t('common.save')}
        </Button>
      </form>
    </SettingGroup>
  );
}

function DirectoryPasswordGroup() {
  const { t } = useTranslation();
  return (
    <SettingGroup title={t('settings.password.title')} hint={t('settings.password.directory')} data-testid="password-directory">
      {null}
    </SettingGroup>
  );
}

function PasswordGroup() {
  const { t } = useTranslation();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNext] = useState('');
  const change = useMutation({
    mutationFn: () => api('POST', '/api/auth/password/change', { currentPassword, newPassword }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
    },
  });
  return (
    <SettingGroup title={t('settings.password.title')}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          change.mutate();
        }}
      >
        <ErrorAlert error={change.error} />
        {change.isSuccess ? <Alert tone="success" title={t('settings.password.success')} /> : null}
        <div className="form-grid">
          <Field label={t('settings.password.current')}>
            {({ id }) => <TextInput id={id} type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} />}
          </Field>
          <Field label={t('settings.password.next')} hint={t('auth.signup.passwordHint')}>
            {({ id, describedBy }) => (
              <TextInput id={id} aria-describedby={describedBy} type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNext(e.target.value)} />
            )}
          </Field>
        </div>
        <Button type="submit" variant="secondary" busy={change.isPending} disabled={!currentPassword || !newPassword}>
          {t('settings.password.submit')}
        </Button>
      </form>
    </SettingGroup>
  );
}

function SessionsGroup() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const sessions = useSessions();
  const revoke = useMutation({
    mutationFn: (id: string) => api('DELETE', `/api/sessions/${id}`),
    onSuccess: (_data, id) => {
      if (sessions.data?.find((s) => s.id === id)?.current) {
        queryClient.clear();
        queryClient.setQueryData(keys.me, null);
        void navigate('/login');
        return;
      }
      void queryClient.invalidateQueries({ queryKey: keys.sessions });
    },
  });
  return (
    <SettingGroup title={t('settings.sessions.title')}>
      <ErrorAlert error={sessions.error ?? revoke.error} />
      {sessions.isPending ? <Skeleton lines={2} /> : null}
      <ul className="rows-list" data-testid="session-list">
        {sessions.data?.map((s) => (
          <li key={s.id} className="rows-list__item">
            <Icons.monitor />
            <span className="rows-list__main">
              <span>
                {s.device ?? t('settings.sessions.unknownDevice')}
                {s.client !== 'browser' ? <span className="muted"> · {t('settings.sessions.app')}</span> : null}
                {s.ipSummary ? <span className="muted"> · {s.ipSummary}</span> : null} {s.current ? <Badge tone="success">{t('settings.sessions.current')}</Badge> : null}
              </span>
              <span className="muted">{t('settings.sessions.lastUsed', { date: new Date(s.lastUsedAt).toLocaleString(i18n.language) })}</span>
            </span>
            <Button size="sm" variant="danger" onClick={() => revoke.mutate(s.id)}>
              {t('settings.sessions.revoke')}
            </Button>
          </li>
        ))}
      </ul>
    </SettingGroup>
  );
}

/** QW-504: what "always allow" allowed, by scope; removing a rule asks again. */
function RulesGroup() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const rules = useQuery({ queryKey: ['permission-rules'], queryFn: () => api<PermissionRuleDto[]>('GET', '/api/permission-rules') });
  const revoke = useMutation({
    mutationFn: (ruleId: string) => api('DELETE', `/api/permission-rules/${ruleId}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['permission-rules'] }),
  });
  return (
    <SettingGroup title={t('agent.rules.title')} hint={t('agent.rules.hint')}>
      <ErrorAlert error={rules.error ?? revoke.error} />
      {rules.data?.length === 0 ? <EmptyState>{t('agent.rules.empty')}</EmptyState> : null}
      <ul className="rows-list" data-testid="rule-list">
        {rules.data?.map((rule) => (
          <li key={rule.id} className="rows-list__item">
            <Icons.policies />
            <span className="rows-list__main">
              <span>{rule.label}</span>
              <span className="muted">{t(`agent.rules.scopes.${rule.scope}`)}</span>
            </span>
            <Button size="sm" variant="secondary" busy={revoke.isPending && revoke.variables === rule.id} onClick={() => revoke.mutate(rule.id)}>
              {t('agent.rules.revoke')}
            </Button>
          </li>
        ))}
      </ul>
    </SettingGroup>
  );
}

/** MM-022: a member's own providers, when the organization allows them (always in a personal space). */
function ProvidersGroup() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const providers = useProviders();
  const models = useModels();
  const routes = useRoutes();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.providers });
    void queryClient.invalidateQueries({ queryKey: keys.models });
  };
  const mine = (providers.data ?? []).filter((p) => p.scope === 'user');
  // what the person's own choices use their models for
  const usage = modelUsage(taskStates(Object.fromEntries((routes.data ?? []).filter((r) => r.scope === 'user').map((r) => [r.callType, r.modelRef]))));
  return (
    <SettingGroup title={t('settingsDialog.account.providers')} hint={t('settings.providers.hint')}>
      {mine.map((p) => (
        <ProviderCard key={p.id} provider={p} models={(models.data?.models ?? []).filter((m) => m.providerId === p.id)} endpoints={providerEndpoints(p.id)} onChanged={refresh} usage={usage} />
      ))}
      <AddProviderInline
        onTest={(draft) => api('POST', '/api/providers/test', ownDraftTest('user', draft))}
        onCreate={async (draft) => {
          await api('POST', '/api/providers', ownDraft('user', draft));
          refresh();
        }}
      />
    </SettingGroup>
  );
}

/**
 * 16c.1 (D-259): the marketing consent given at sign-up, withdrawn (or given again) here, each change with its time;
 * a marketing mail's own link does the same. Only where people sign up by e-mail (bireysel).
 */
function MarketingGroup() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const consents = useQuery({ queryKey: ['me', 'consents'], queryFn: () => api<ConsentsDto>('GET', '/api/me/consents') });
  const set = useMutation({
    mutationFn: (marketing: boolean) => api<ConsentsDto>('PUT', '/api/me/consents/marketing', { marketing }),
    onSuccess: (data) => queryClient.setQueryData(['me', 'consents'], data),
  });
  const data = consents.data;
  if (data && !data.recorded) return null;
  const changed = data?.changedAt ? new Date(data.changedAt).toLocaleString(i18n.language, { dateStyle: 'long', timeStyle: 'short' }) : null;
  return (
    <SettingGroup title={t('b2cModels.consent.title')}>
      <ErrorAlert error={consents.error ?? set.error} />
      <SettingRow label={t('b2cModels.consent.on')} hint={[t('b2cModels.consent.hint'), changed ? t('b2cModels.consent.changed', { date: changed }) : null].filter(Boolean).join(' ')} data-testid="settings-marketing">
        {({ id, hintId }) => (
          <Switch id={id} aria-describedby={hintId} checked={data?.marketing ?? false} disabled={!data || set.isPending} onChange={(e) => set.mutate(e.target.checked)} data-testid="marketing-consent" />
        )}
      </SettingRow>
      {set.isSuccess ? (
        <p className="setting-row__hint" role="status" data-testid="marketing-consent-saved">
          {set.data.marketing ? t('b2cModels.consent.given') : t('b2cModels.consent.withdrawn')}
        </p>
      ) : null}
    </SettingGroup>
  );
}

export function AccountSection({ me }: { me: MeResponse }) {
  const tenant = useTenant();
  const capabilities = useCapabilities();
  // Phase 16b (D-285): the bireysel edition has no own model providers
  const ownProviders = capabilities.ownModelProviders && (me.tenant.kind === 'personal' || Boolean(tenant.data?.allowPersonalProviders));
  return (
    <div className="settings-groups" data-testid="settings-account">
      <ProfileGroup me={me} />
      {/* Phase 13d: a company account's password is the directory's, never typed here */}
      {me.account?.kind === 'directory' ? <DirectoryPasswordGroup /> : <PasswordGroup />}
      <SessionsGroup />
      {capabilities.publicSignup ? <MarketingGroup /> : null}
      {/* Phase 15e: Bağlı hesaplar (Microsoft 365), only where there is one to see or connect */}
      <LinkedAccountsGroup />
      <RulesGroup />
      {ownProviders ? <ProvidersGroup /> : null}
    </div>
  );
}

type Permission = NotificationPermission | 'unsupported';

/** Where the user hears about cards and finished work (SA-033). */
export function NotificationsSection() {
  const { t } = useTranslation();
  const [permission, setPermission] = useState<Permission>(() => (typeof Notification === 'undefined' ? 'unsupported' : Notification.permission));
  return (
    <div className="settings-rows" data-testid="settings-notifications">
      <SettingRow label={t('settingsDialog.notifications.browser')} hint={t(`settingsDialog.notifications.state.${permission}`)}>
        {({ id, hintId }) =>
          permission === 'default' ? (
            <Button id={id} aria-describedby={hintId} variant="secondary" onClick={() => void Notification.requestPermission().then(setPermission)} data-testid="notifications-allow">
              {t('settingsDialog.notifications.allow')}
            </Button>
          ) : (
            <Badge tone={permission === 'granted' ? 'success' : 'neutral'}>{t(`settingsDialog.notifications.badge.${permission}`)}</Badge>
          )
        }
      </SettingRow>
      <SettingRow label={t('settingsDialog.notifications.center')} hint={t('settingsDialog.notifications.centerHint')}>
        {() => <Icons.bell />}
      </SettingRow>
      <SettingRow label={t('settingsDialog.notifications.what')} stacked>
        {() => (
          <ul className="check-list">
            {(['approval', 'question', 'task', 'failed'] as const).map((kind) => (
              <li key={kind}>
                <Icons.check size={16} />
                {t(`settingsDialog.notifications.kinds.${kind}`)}
              </li>
            ))}
          </ul>
        )}
      </SettingRow>
    </div>
  );
}

function MemoryRow({ memory }: { memory: MemoryDto }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<string | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: keys.memories });
  const save = useMutation({
    mutationFn: (content: string) => api('PATCH', `/api/memories/${memory.id}`, { content }),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: () => api('DELETE', `/api/memories/${memory.id}`), onSuccess: refresh });
  return (
    <li className="rows-list__item memory-item" data-testid="memory-item">
      <Icons.memory />
      {editing !== null ? (
        <form
          className="memory-item__edit"
          onSubmit={(e) => {
            e.preventDefault();
            if (editing.trim()) save.mutate(editing.trim());
          }}
        >
          <TextArea value={editing} onChange={(e) => setEditing(e.target.value)} aria-label={t('memory.edit')} autoFocus data-testid="memory-edit-input" />
          <div className="row">
            <Button size="sm" type="submit" busy={save.isPending}>
              {t('common.save')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </Button>
          </div>
          <ErrorAlert error={save.error} />
        </form>
      ) : (
        <span className="rows-list__main">
          <span className="memory-item__content">{memory.content}</span>
          <span className="muted">
            {memory.source === 'agent' ? t('memory.fromConversation') : t('memory.byYou')} · {new Date(memory.updatedAt).toLocaleDateString(i18n.language)}
            {memory.scope === 'workspace' ? ` · ${t('memory.workspaceOnly')}` : ''}
            {memory.conversationId && memory.conversationTitle ? (
              // D-189: the conversation it came from, by its title
              <>
                {' · '}
                <Link to={`/c/${memory.conversationId}`} data-testid="memory-conversation">
                  {memory.conversationTitle}
                </Link>
              </>
            ) : memory.conversationId ? (
              <>
                {' · '}
                <Link to={`/c/${memory.conversationId}`}>{t('memory.openConversation')}</Link>
              </>
            ) : null}
          </span>
        </span>
      )}
      {editing === null ? (
        <span className="row">
          <Button size="sm" variant="ghost" onClick={() => setEditing(memory.content)} data-testid="memory-edit">
            {t('memory.edit')}
          </Button>
          <Button size="sm" variant="ghost" busy={remove.isPending} onClick={() => remove.mutate()} data-testid="memory-delete">
            {t('common.delete')}
          </Button>
        </span>
      ) : null}
      <ErrorAlert error={remove.error} />
    </li>
  );
}

/**
 * What Oppy remembers (D-16, SA-061, QW-BUG-035): only what the user asked
 * it to remember, visible here, editable and removable; memory can be
 * turned off. Documents and conversations are never copied here on their own.
 */
export function MemorySection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const memories = useMemories();
  const preferences = usePreferences();
  const save = useSavePreferences();
  const [content, setContent] = useState('');
  const add = useMutation({
    mutationFn: () => api<MemoryDto>('POST', '/api/memories', { content: content.trim() }),
    onSuccess: () => {
      setContent('');
      void queryClient.invalidateQueries({ queryKey: keys.memories });
    },
  });
  const prefs = preferences.data;
  return (
    <div className="settings-groups memory-page" data-testid="settings-memory">
      <p className="muted">{t('memory.intro')}</p>
      {prefs && !prefs.memoryAllowed ? <Alert tone="warning" title={t('memory.disabledByAdmin')} /> : null}
      {prefs && prefs.memoryAllowed ? (
        <label className="ui-switch-row setting-switch">
          <span>
            <strong>{t('memory.enabled')}</strong>
            <span className="muted"> — {t('memory.enabledHint')}</span>
          </span>
          <Switch checked={prefs.memoryEnabled} onChange={(e) => save.mutate({ memoryEnabled: e.target.checked })} data-testid="memory-enabled-switch" />
        </label>
      ) : null}
      <ErrorAlert error={save.error ?? memories.error} />
      <SettingGroup title={t('memory.add')}>
        <form
          className="setting-row__form"
          onSubmit={(e) => {
            e.preventDefault();
            if (content.trim()) add.mutate();
          }}
        >
          <TextArea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t('memory.addPlaceholder')} aria-label={t('memory.add')} data-testid="memory-add-input" />
          <ErrorAlert error={add.error} />
          <Button type="submit" size="sm" busy={add.isPending} disabled={!content.trim() || prefs?.memoryAllowed === false} data-testid="memory-add">
            {t('memory.addButton')}
          </Button>
        </form>
      </SettingGroup>
      <SettingGroup title={t('memory.stored', { count: memories.data?.length ?? 0 })}>
        {memories.isPending ? <Skeleton lines={3} /> : null}
        {memories.data?.length === 0 ? <EmptyState icon={<Icons.memory />} title={t('memory.empty')} /> : null}
        <ul className="rows-list" data-testid="memory-list">
          {memories.data?.map((memory) => (
            <MemoryRow key={memory.id} memory={memory} />
          ))}
        </ul>
      </SettingGroup>
    </div>
  );
}

/** The organization's e-mail: what goes out today; alerts come with the limits' e-mails. */
export function EmailSection() {
  const { t } = useTranslation();
  const me = useMe().data;
  return (
    <div className="settings-rows" data-testid="settings-email">
      <SettingRow label={t('settingsDialog.email.sent')} stacked>
        {() => (
          <ul className="check-list">
            {(['verify', 'reset', 'invite'] as const).map((kind) => (
              <li key={kind}>
                <Icons.mail size={16} />
                {t(`settingsDialog.email.kinds.${kind}`)}
              </li>
            ))}
          </ul>
        )}
      </SettingRow>
      <SettingRow label={t('settingsDialog.email.language')} hint={t('settingsDialog.email.languageHint')}>
        {() => <Badge>{me?.user.locale === 'en' ? t('lang.en') : t('lang.tr')}</Badge>}
      </SettingRow>
      <SettingRow label={t('settingsDialog.email.budget')} hint={t('settingsDialog.email.budgetHint')}>
        {() => <Badge>{t('settingsDialog.email.soon')}</Badge>}
      </SettingRow>
    </div>
  );
}
